import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Upload, Loader2, X, Ruler, Eye } from 'lucide-react';
import api, { medienApi, fotosApi } from '../services/api';
import FotoAnnotator, { type Stufe } from './FotoAnnotator';
import MedienBild, { useMedienUrl } from './MedienBild';
import Can, { usePermission } from './Can';
import { Fehlermeldung } from '../pages/Auth/AuthLayout';

/**
 * Fotos eines Elements oder Mangels: hochladen, ansehen, markieren.
 *
 * Die Kamera-Schaltfläche nutzt `capture="environment"`. Auf dem Handy
 * öffnet das unmittelbar die Rückkamera statt des Dateiwählers -- vor Ort
 * spart das bei jeder Aufnahme drei Schritte, und es sind Hunderte am
 * Tag.
 */
export function FotoPanel({
  elementId,
  mangelId,
  projektId,
}: {
  elementId?: number;
  mangelId?: number;
  projektId?: number;
}) {
  const qc = useQueryClient();
  const { hatRecht } = usePermission();
  const dateiwahl = useRef<HTMLInputElement>(null);
  const kamera = useRef<HTMLInputElement>(null);
  const [offen, setOffen] = useState<number | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [hinweise, setHinweise] = useState<string[]>([]);

  const bezug = { element_id: elementId, mangel_id: mangelId, projekt_id: projektId };
  const schluessel = ['fotos', elementId ?? 0, mangelId ?? 0, projektId ?? 0];

  const { data: fotos, isLoading } = useQuery({
    queryKey: schluessel,
    queryFn: () =>
      elementId
        ? fotosApi.getByElement(elementId)
        : mangelId
          ? fotosApi.getByMangel(mangelId)
          : fotosApi.getByProjekt(projektId!),
  });

  const hochladen = useMutation({
    mutationFn: (dateien: File[]) => medienApi.hochladen(dateien, bezug),
    onSuccess: (ergebnis) => {
      setFehler(null);
      // Übersprungene Dateien werden GENANNT und nicht verschwiegen: ein
      // stillschweigend fehlendes Foto fällt erst im Bericht auf.
      setHinweise(
        (ergebnis.uebersprungen ?? []).map(
          (u: any) => `${u.dateiname}: ${u.grund}`
        )
      );
      void qc.invalidateQueries({ queryKey: schluessel });
    },
    onError: setFehler,
  });

  const loeschen = useMutation({
    mutationFn: (id: number) => medienApi.loeschen(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: schluessel }),
  });

  function gewaehlt(e: React.ChangeEvent<HTMLInputElement>): void {
    const dateien = Array.from(e.target.files ?? []);
    if (dateien.length > 0) hochladen.mutate(dateien);
    // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
    e.target.value = '';
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Fotos {fotos ? `(${fotos.length})` : ''}
        </h3>

        <Can recht="fotos.hochladen">
          <div className="flex gap-2">
            <input
              ref={kamera}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              hidden
              onChange={gewaehlt}
            />
            <input
              ref={dateiwahl}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={gewaehlt}
            />
            <button
              type="button"
              onClick={() => kamera.current?.click()}
              disabled={hochladen.isPending}
              className="flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {hochladen.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
              Aufnehmen
            </button>
            <button
              type="button"
              onClick={() => dateiwahl.current?.click()}
              disabled={hochladen.isPending}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <Upload className="h-4 w-4" />
              Auswählen
            </button>
          </div>
        </Can>
      </div>

      <Fehlermeldung fehler={fehler} />

      {hinweise.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">Nicht übernommen:</p>
          <ul className="mt-1 list-inside list-disc">
            {hinweise.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Wird geladen…</p>
      ) : (fotos ?? []).length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Noch keine Fotos.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {fotos!.map((f: any) => (
            <figure key={f.id} className="group relative">
              <button
                type="button"
                onClick={() => setOffen(f.id)}
                className="block w-full overflow-hidden rounded-lg border border-gray-200"
              >
                {/*
                  Kein blankes <img>: /api/medien verlangt seit Phase 0.5
                  den Bearer-Token, ein direktes src bekaeme 401.
                */}
                <MedienBild
                  quelle={f.thumbnail_url || f.foto_url}
                  alt={f.beschreibung || f.dateiname || 'Foto'}
                  className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
                />
              </button>
              {f.massstab_px_pro_mm && (
                <span
                  title="Für dieses Foto ist ein Maßstab gesetzt"
                  className="absolute left-1.5 top-1.5 rounded bg-black/60 p-1 text-white"
                >
                  <Ruler className="h-3 w-3" />
                </span>
              )}
              {hatRecht('fotos.loeschen') && (
                <button
                  type="button"
                  onClick={() => loeschen.mutate(f.id)}
                  title="Foto löschen"
                  className="absolute right-1.5 top-1.5 rounded bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </figure>
          ))}
        </div>
      )}

      {offen != null && (
        <FotoDialog fotoId={offen} onSchliessen={() => setOffen(null)} />
      )}
    </section>
  );
}

/** Vollansicht mit Annotator. */
function FotoDialog({
  fotoId,
  onSchliessen,
}: {
  fotoId: number;
  onSchliessen: () => void;
}) {
  const qc = useQueryClient();
  const { hatRecht } = usePermission();
  const [fehler, setFehler] = useState<unknown>(null);
  const [kundenfassung, setKundenfassung] = useState(false);

  const { data } = useQuery({
    queryKey: ['foto', fotoId],
    queryFn: () => medienApi.annotationen(fotoId),
  });
  const { data: alle } = useQuery({
    queryKey: ['foto-datei', fotoId],
    queryFn: () => fotosApi.getById(fotoId),
  });

  /*
   * Der Annotator zeichnet selbst und braucht eine URL, die ein
   * <img>-Element laden kann. Eine Objekt-URL erfuellt das; die
   * API-Adresse nicht, weil ihr der Token fehlte.
   */
  const { url: annotierUrl } = useMedienUrl(alle?.foto_url);

  const neuLaden = () => {
    void qc.invalidateQueries({ queryKey: ['foto', fotoId] });
  };

  /**
   * Das Original in einem neuen Fenster zeigen.
   *
   * Der Umweg ueber den Abruf ist noetig, weil ein einfacher Verweis
   * keinen Token mitschickt. Die Objekt-URL wird nach einer Minute
   * freigegeben -- frueher waere das Fenster leer, spaeter bliebe das
   * Bild unnoetig im Speicher.
   */
  async function originalOeffnen(pfad: string): Promise<void> {
    try {
      const antwort = await api.get(pfad.replace(/^\/api/, ''), { responseType: 'blob' });
      const url = URL.createObjectURL(antwort.data as Blob);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setFehler(e);
    }
  }

  async function massstabSetzen(laengePx: number): Promise<void> {
    const mm = window.prompt(
      'Wie lang ist die gezogene Strecke in Millimetern?\n\n' +
        'Ziehen Sie über etwas, dessen Maß Sie kennen — einen Zollstock, ' +
        'ein Normmaß, eine bekannte Türblattbreite.'
    );
    if (!mm) return;
    const referenz = window.prompt(
      'Worauf beziehen Sie sich? (erscheint später im Nachweis)',
      'Zollstock'
    );
    if (!referenz) return;

    try {
      await medienApi.massstabSetzen(fotoId, {
        laenge_px: laengePx,
        laenge_mm: Number(mm.replace(',', '.')),
        referenz,
      });
      setFehler(null);
      neuLaden();
    } catch (e) {
      setFehler(e);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => e.target === e.currentTarget && onSchliessen()}
    >
      <div className="max-h-full w-full max-w-4xl overflow-auto rounded-lg bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            {alle?.dateiname ?? 'Foto'}
          </h3>
          <button
            type="button"
            onClick={onSchliessen}
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <Fehlermeldung fehler={fehler} />

        {data && alle && annotierUrl && (
          <FotoAnnotator
            bildUrl={annotierUrl}
            bildBreite={data.foto.breite || 1000}
            annotationen={data.annotationen}
            massstabPxProMm={data.foto.massstab_px_pro_mm}
            darfBearbeiten={hatRecht('fotos.annotieren')}
            onAnlegen={async (a) => {
              try {
                await medienApi.annotationAnlegen(fotoId, a);
                setFehler(null);
                neuLaden();
              } catch (e) {
                setFehler(e);
              }
            }}
            onLoeschen={async (id) => {
              await medienApi.annotationLoeschen(id);
              neuLaden();
            }}
            onMassstabSetzen={(px) => void massstabSetzen(px)}
            onSichtbarkeit={async (id, sichtbarkeit: Stufe) => {
              try {
                await medienApi.annotationAendern(id, { sichtbarkeit });
                setFehler(null);
                neuLaden();
              } catch (e) {
                setFehler(e);
              }
            }}
            onBestaetigen={async (id) => {
              try {
                await medienApi.annotationBestaetigen(id);
                setFehler(null);
                neuLaden();
              } catch (e) {
                setFehler(e);
              }
            }}
            onKiVorschlagen={
              hatRecht('system.ki_nutzen')
                ? async () => {
                    try {
                      await medienApi.kiMarkierungen(fotoId);
                      setFehler(null);
                      neuLaden();
                    } catch (e) {
                      setFehler(e);
                    }
                  }
                : undefined
            }
          />
        )}

        {/*
          Die Kundenfassung ist keine zweite Ansicht desselben Bildes,
          sondern die Antwort auf die Frage „was geht tatsächlich hinaus".
          Eine Schwärzung, deren Wirkung sich erst im PDF beim Kunden
          prüfen lässt, wäre eine Zusicherung auf Verdacht.
        */}
        <div className="mt-3 border-t border-gray-200 pt-3">
          <button
            type="button"
            onClick={() => setKundenfassung((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
          >
            <Eye className="h-4 w-4" />
            {kundenfassung ? 'Kundenfassung ausblenden' : 'Kundenfassung ansehen'}
          </button>
          {kundenfassung && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-gray-500">
                So geht das Bild in ein Kundendokument: geschwärzte Bereiche sind in
                den Bilddaten überschrieben, interne Markierungen und unbestätigte
                KI-Vorschläge fehlen.
              </p>
              <MedienBild
                quelle={`/api/medien/${fotoId}/kundenfassung`}
                alt="Kundenfassung"
                className="w-full rounded-lg border border-gray-200"
              />
            </div>
          )}
        </div>

        {alle?.original_url && (
          <p className="mt-3 text-xs text-gray-500">
            {/*
              Ein <a href> schickt keinen Token mit und bekaeme 401.
              Deshalb wird das Original geholt und als Objekt-URL in einem
              neuen Fenster geoeffnet.
            */}
            <button
              type="button"
              onClick={() => void originalOeffnen(alle.original_url as string)}
              className="text-primary-600 hover:underline"
            >
              Original öffnen
            </button>{' '}
            — es bleibt unverändert erhalten; Markierungen liegen darüber.
          </p>
        )}
      </div>
    </div>
  );
}

export default FotoPanel;
